import each from 'lodash/each';
import isUndefined from 'lodash/isUndefined';
import isArray from 'lodash/isArray';
import find from 'lodash/find';

import store from '@Store';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import uuid from 'uuid/v4';

import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { citations, SORT_BY_RELEVANCE_FIELD, SORT_BY_RELEVANCE_ORDER } from '@Constants';

import AppStorage from '@Services/AppStorage';
import BatchSearchRunner from '@Services/BatchSearchRunner';
import { BATCH_FILE } from '@Services/BatchSearchRunner/constants';
import * as SearchImportFile from '@Services/SearchImportFile';

import {
  batchModalSetCurrentStructureOptionsReducerType,
  typeOfBatchModal,
  batchModalSetBatchFileReducerType,
  batchModalSetOverwriteOptionsReducerType,
} from './BatchModalConstants';
import {
  searchContext as searchContextSelector,
  structures as structuresSelector,
  batchFile as batchFileSelector,
  overwriteOptions as overwriteOptionsSelector,
  currentStructureOptions as currentStructureOptionsSelector,
} from './BatchModalSelectors';

import { initialState } from './BatchModalReducer';

export const openBatchModal = (batchFile) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfBatchModal,
      dialogType: typeOfBatchModal,
      movable: false,
      backdrop: true,
      batchFile,
    })
  );
};

export const closeBatchModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfBatchModal,
    })
  );
};

export const dispatchOpenBatchModal = (...args) => {
  store.dispatch(openBatchModal(...args));
};

export function setBatchFile(batchFile) {
  return {
    type: batchModalSetBatchFileReducerType,
    payload: { batchFile },
  };
}

export function setCurrentStructureOptions(currentStructureOptions) {
  return {
    type: batchModalSetCurrentStructureOptionsReducerType,
    payload: { currentStructureOptions },
  };
}

export function setOverwriteOptions(overwriteOptions) {
  return {
    type: batchModalSetOverwriteOptionsReducerType,
    payload: { overwriteOptions },
  };
}

export const resetState = () => (dispatch) => {
  dispatch(setBatchFile(initialState.get('batchFile')));
  dispatch(setOverwriteOptions(initialState.get('overwriteOptions')));
  dispatch(setCurrentStructureOptions(initialState.get('currentStructureOptions')));
};

export const initialize = (batchFile) => (dispatch) => {
  dispatch(resetState());
  if (!batchFile || !batchFile.validQueriesCount) {
    SearchImportFile.showErrorAlert();
    dispatch(closeBatchModal());
    return;
  }

  dispatch(setBatchFile(batchFile));
};

export const updateOptions = () => {
  const { structureOptions } = BATCH_FILE;

  return (dispatch, getState) => {
    const structures = structuresSelector(getState());
    const overwriteOptions = overwriteOptionsSelector(getState());
    const currentStructureOptions = currentStructureOptionsSelector(getState());

    if (overwriteOptions) {
      // overwrite all structure options
      each(structures, (structure) => {
        // eslint-disable-next-line no-param-reassign
        structure.options = currentStructureOptions;
      });
      return;
    }
    // check if structure has query type predefined eg. searchBy 'exact'/'similarity'/'substructure' add it in case if not.
    each(structures, (structure) => {
      if (isArray(structure.options)) {
        // if no query type option defined - add default query type option
        const searchByOption = find(structure.options, { name: 'searchBy' });
        if (isUndefined(searchByOption)) {
          structure.options.unshift(structureOptions.DEFAULT_QUERY_TYPE_OPTION);
        }
        return;
      }
      // if options are not defined or empty - just set a default query type
      // eslint-disable-next-line no-param-reassign
      structure.options = [structureOptions.DEFAULT_QUERY_TYPE_OPTION];
    });
  };
};

export const send = () => async (dispatch, getState) => {
  const searchContext = searchContextSelector(getState());
  const batchFile = batchFileSelector(getState());
  const overwriteOptions = overwriteOptionsSelector(getState());
  const currentStructureOptions = currentStructureOptionsSelector(getState());
  const context = searchContext.serverAlias;
  const sortByRelevanceDefaultEnabled = !Auth.isFeatureEnabled(FEATURES.enableSortByPublicationYearDefault);

  const payload = {
    context,
    searchId: uuid(),
    ...(context === citations &&
      sortByRelevanceDefaultEnabled && {
        sort: SORT_BY_RELEVANCE_FIELD,
        order: SORT_BY_RELEVANCE_ORDER,
      }),
    queries: batchFile.queries,
  };

  if (overwriteOptions) {
    payload.options = currentStructureOptions;
  }

  await BatchSearchRunner.sendRequest(payload, batchFile)
    .then(() => dispatch(closeBatchModal()))
    .catch(Logger.error);
};

export const search = (context) => (dispatch, getState) => {
  if (context) {
    AppStorage.instance.set('qbSearchDB', context);
  }
  const searchContext = searchContextSelector(getState());

  if (!searchContext) {
    return;
  }
  const structures = structuresSelector(getState());

  if (structures.length) {
    dispatch(updateOptions());
  }

  dispatch(send());
};
